import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { BRANDS as BRAND_IDS } from "@/constants/testIds";
import { BRANDS, MANIFESTOS } from "@/data/brands";
import { Spaced } from "@/components/site/Hero";
import { useT } from "@/i18n/LanguageContext";

// A single character with its own useTransform hook so hook count stays constant
// per component instance even when text length (i18n) changes.
function RevealChar({ char, i, total, progress }) {
  const opacity = useTransform(
    progress,
    [i / total, (i + 1) / total],
    [0.15, 1]
  );
  return <motion.span style={{ opacity }}>{char}</motion.span>;
}

// Reveal each character one by one as the paragraph enters view.
function LetterReveal({ text, className = "" }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.85", "start 0.15"],
  });
  const chars = text.split("");
  return (
    <p ref={ref} key={text} className={className}>
      {chars.map((ch, i) => (
        <RevealChar
          key={`${ch}-${i}`}
          char={ch}
          i={i}
          total={chars.length}
          progress={scrollYProgress}
        />
      ))}
    </p>
  );
}

export default function BrandsShowcase() {
  const { t } = useT();
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  // Scroll tracker to update the active brand as user scrolls horizontally
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const handler = () => {
      const cards = el.querySelectorAll("[data-brand-card]");
      const center = el.scrollLeft + el.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((c, i) => {
        const mid = c.offsetLeft + c.offsetWidth / 2;
        const d = Math.abs(mid - center);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      setActive(best);
    };
    el.addEventListener("scroll", handler, { passive: true });
    // Center the first card (Mamm) on initial mount so it lines up with the
    // brand info below instead of hugging the left edge.
    const cards = el.querySelectorAll("[data-brand-card]");
    if (cards[0]) {
      const target = cards[0].offsetLeft + cards[0].offsetWidth / 2 - el.clientWidth / 2;
      el.scrollTo({ left: target, behavior: "instant" });
    }
    handler();
    return () => el.removeEventListener("scroll", handler);
  }, []);

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelectorAll("[data-brand-card]")[i];
    if (card) {
      const target = card.offsetLeft + card.offsetWidth / 2 - el.clientWidth / 2;
      el.scrollTo({ left: target, behavior: "smooth" });
    }
  };

  const activeBrand = BRANDS[active];

  return (
    <section id="brands" data-testid={BRAND_IDS.root} className="relative">
      {/* Intro banner */}
      <section className="relative bg-white py-24 lg:py-32 overflow-hidden">
        <img
          aria-hidden
          src="/hupla-emblem.svg"
          alt=""
          className="pointer-events-none select-none absolute -right-40 top-1/2 -translate-y-1/2 w-[720px] max-w-none opacity-[0.05]"
        />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center font-display font-black text-[#001F39] leading-[1.05] text-[clamp(2rem,5vw,4.5rem)]"
          >
            <Spaced text={t("brands.title").split(" ").slice(0, 3).join(" ")} />
            <br />
            <Spaced text={t("brands.title").split(" ").slice(3).join(" ")} />
          </motion.h2>

          {/* Single flex row — all 7 products share the SAME bottom baseline; each
              rendered at height directly proportional to its real-world mm value. */}
          <div className="mt-16 flex items-end justify-center flex-nowrap gap-3 md:gap-4 w-full max-w-[1600px] mx-auto px-4 overflow-x-auto">
            {BRANDS.slice(0, 8).map((b, i) => (
              <motion.img
                key={b.slug}
                src={b.frontImage || b.image}
                alt={b.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                style={{
                  height: `clamp(${(b.heightMm || 200) * 0.55}px, ${(b.heightMm || 200) * 0.24}vw, ${(b.heightMm || 200) * 1.8}px)`,
                  width: "auto",
                  flexShrink: 0,
                }}
                className="object-contain drop-shadow-[0_30px_45px_rgba(0,0,0,0.20)]"
              />
            ))}
          </div>

          <div className="mt-24 max-w-4xl mx-auto">
            <LetterReveal
              text={t("brands.manifesto1")}
              className="text-center text-2xl md:text-3xl font-display font-medium leading-[1.5] text-[#001F39]"
            />
          </div>

          <p className="mt-14 text-center overline text-[#25bfcf]">
            {t("brands.subtitle")}
          </p>
        </div>
      </section>

      {/* Horizontal brand rail — like the hupla.world "drag" carousel */}
      <section
        className="relative overflow-hidden bg-white"
      >
        <img
          aria-hidden
          src="/hupla-emblem.svg"
          alt=""
          className="pointer-events-none select-none absolute -left-40 top-1/2 -translate-y-1/2 w-[720px] max-w-none opacity-[0.05]"
        />
        {/* Brand accent strip at top */}
        <div
          className="h-2 w-full transition-colors duration-700"
          style={{ background: activeBrand.palette.accent }}
        />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 pb-10">
          <div className="flex items-baseline justify-between gap-6 mb-10">
            <p
              className="overline transition-colors duration-500"
              style={{ color: activeBrand.palette.accent }}
            >
              {t("brands.portfolio")} · {String(active + 1).padStart(2, "0")} {t("brands.of")}{" "}
              {String(BRANDS.length).padStart(2, "0")}
            </p>
            <p
              className="text-sm font-semibold uppercase tracking-widest text-neutral-400"
            >
              {t("brands.dragSwipe")} →
            </p>
          </div>

          <div
            ref={trackRef}
            className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-6 scrollbar-hide"
            style={{
              scrollPaddingLeft: "50%",
              scrollPaddingRight: "50%",
            }}
          >
            {/* leading spacer — wide enough to let the first card (Mamm) center */}
            <div className="shrink-0 w-[calc(50vw-270px)] min-w-[24px]" />
            {BRANDS.map((brand, i) => (
              <Link
                key={brand.slug}
                to={`/brands/${brand.slug}`}
                data-brand-card
                data-testid={BRAND_IDS.card(brand.slug)}
                id={`brand-${brand.slug}`}
                onClick={(e) => {
                  // If it's not the active/centered card, first center it instead of navigating
                  if (active !== i) {
                    e.preventDefault();
                    goTo(i);
                  }
                }}
                className="shrink-0 snap-center focus:outline-none group cursor-pointer"
                style={{ width: "min(540px, 82vw)" }}
              >
                <div
                  className={`relative rounded-[32px] overflow-hidden transition-all duration-500 ${
                    active === i
                      ? "scale-100 opacity-100"
                      : "scale-90 opacity-60"
                  }`}
                >
                  <div className="aspect-[1/1] flex items-center justify-center p-2">
                    <img
                      src={brand.frontImage || brand.image}
                      alt={brand.name}
                      className="w-auto object-contain drop-shadow-[0_25px_35px_rgba(0,0,0,0.35)]"
                      style={{
                        // Height scales with real-world mm: tallest product (Desser 340mm)
                        // gets 100% of card, others shrink proportionally
                        height: `${((brand.heightMm || 200) / 340) * 100}%`,
                        maxHeight: "100%",
                      }}
                    />
                  </div>
                </div>
              </Link>
            ))}
            <div className="shrink-0 w-[calc(50vw-270px)] min-w-[24px]" />
          </div>

          {/* Active brand: logo + tagline + explore CTA */}
          <div className="text-center mt-2 min-h-[8rem]">
            {activeBrand.logo && (() => {
              // Buzzer, Cafeo, Aperina and White Elephant have wider/taller
              // wordmarks that need extra room to read well below the card.
              const BIG_LOGO = new Set(["buzzer", "cafeo", "aperina", "white-elephant"]);
              const sizeCls = BIG_LOGO.has(activeBrand.slug)
                ? "h-20 md:h-24"
                : "h-12 md:h-14";
              return (
                <motion.img
                  key={active + "-logo"}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  src={activeBrand.logo}
                  alt={activeBrand.name}
                  className={`mx-auto mb-2 ${sizeCls} w-auto object-contain`}
                />
              );
            })()}
            <motion.p
              key={active + "-cat"}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="overline mb-2"
              style={{ color: activeBrand.palette.accent }}
            >
              {t(`brandContent.${activeBrand.slug}.category`)}
            </motion.p>
            <motion.h3
              key={active + "-tag"}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="font-editorial italic text-xl md:text-2xl max-w-3xl mx-auto leading-tight text-[#001F39]"
            >
              &ldquo;{t(`brandContent.${activeBrand.slug}.tagline`)}&rdquo;
            </motion.h3>
            <motion.div
              key={active + "-cta"}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="mt-3 flex justify-center"
            >
              <Link
                to={`/brands/${activeBrand.slug}`}
                data-testid={`brands-explore-${activeBrand.slug}`}
                className="inline-flex items-center gap-2 rounded-full pl-6 pr-2 py-2 text-sm font-medium text-white transition-transform hover:scale-[1.03]"
                style={{ background: activeBrand.palette.accent }}
              >
                {t("brands.exploreBrand")} {activeBrand.name}
                <span
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full"
                  style={{
                    background: "rgba(255,255,255,0.9)",
                    color: activeBrand.palette.accent,
                  }}
                >
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
          </div>

          {/* Dot nav */}
          <div className="mt-10 flex justify-center gap-2 pb-6">
            {BRANDS.map((b, i) => (
              <button
                key={b.slug}
                onClick={() => goTo(i)}
                aria-label={`Go to ${b.name}`}
                className="w-2.5 h-2.5 rounded-full transition-all"
                style={{
                  background:
                    active === i ? activeBrand.palette.accent : "rgba(0,0,0,0.15)",
                  width: active === i ? "1.75rem" : "0.6rem",
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Second manifesto */}
      <section className="relative bg-white py-28 lg:py-36 overflow-hidden">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <p className="overline text-[#25bfcf] text-center mb-10">
            {t("brands.livingCulture")}
          </p>
          <p className="text-center text-xl md:text-2xl leading-[1.65] text-[#001F39] mb-16">
            {t("brands.cities")}
          </p>
          <LetterReveal
            text={t("brands.manifesto2")}
            className="text-center text-2xl md:text-3xl font-display font-medium leading-[1.5] text-[#001F39]"
          />
        </div>
      </section>
    </section>
  );
}
